import { FunctionComponent, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { BodyText, Div, Title, Button, Flex, Divider, Price } from '@gaiads/telia-react-component-library';
import { SelectUserInput } from '@cdaas/reusable-components-daas';
import { useTranslation } from 'react-i18next';
import { AllowanceType } from '@cdaas/api-clients/dist/asset-user-openapi';

import { User } from '../../../../types';
import { RootState } from '../../../../store/reducers';
import { shopSetOrderOnBehalfUser, shopClearOrderOnBehalfUser, shopClearOrderForOrganization } from '../../../../store/actions';
import { getUsersFromOrganizationOrUserGroup, getAUser, isSingleDevicePolicyInEffectForUser, isNumeric } from '../../../../utils';
import { useHistory } from 'react-router-dom';

const OrderingOnBehalfInput: FunctionComponent = () => {
  const [usersForOOB, setUsersForOOB] = useState<User[]>([]);
  const [resetSelected, setResetSelected] = useState<boolean>(false);

  const history = useHistory();

  const orderingOnBehalfUserData = useSelector((state: RootState) => state.shop.orderOnBehalfUser);
  const orderingForOrganization = useSelector((state: RootState) => state.shop.orderForOrganization);
  const ownOrganization = useSelector((state: RootState) => state.authentication.user?.organization);
  const selectedOrganization = useSelector((state: RootState) => state.authentication.selectedOrganization);

  const dispatch = useDispatch();
  const setOrderingOnBehalfUserData = (data: User) => dispatch(shopSetOrderOnBehalfUser(data));
  const clearOrderingOnBehalfUserData = () => dispatch(shopClearOrderOnBehalfUser());
  const clearOrderingForOrganization = () => dispatch(shopClearOrderForOrganization());

  const { t } = useTranslation('product');

  const clearSelection = () => {
    setResetSelected(!resetSelected);
    clearOrderingOnBehalfUserData();
    clearOrderingForOrganization();
    history.push('/home');
  };

  const getUsersForOOB = async (): Promise<void> => {
    const users = await getUsersFromOrganizationOrUserGroup('organization');
    if (users) {
      setUsersForOOB(users);
    }
  };

  useEffect(() => {
    setResetSelected(!resetSelected);
    setUsersForOOB([]);
    getUsersForOOB();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrganization]);

  const getSelectedOOBUser = async (selectedUser: User): Promise<void> => {
    const userId = selectedUser?.id;
    const userGroupId = selectedUser?.user_group?.id;
    if (!userId || !userGroupId) {
      return;
    }
    const user = await getAUser(userId, userGroupId);
    if (user) {
      clearOrderingForOrganization();
      setOrderingOnBehalfUserData(user);
    }
  };

  const renderAllowance = () => {
    if (
      orderingOnBehalfUserData.userToOrderFor?.allowance_type === AllowanceType.Monthly ||
      orderingOnBehalfUserData.userToOrderFor?.allowance_type === AllowanceType.OneTime
    ) {
      if (isNumeric(orderingOnBehalfUserData.userToOrderFor.allowance)) {
        return (
          <Price
            value={Number(orderingOnBehalfUserData.userToOrderFor.allowance)}
            description={t('allowance.includingVat')}
            unit={orderingOnBehalfUserData.userToOrderFor.organization?.locale.currency.toUpperCase()}
            unitSpecifier={orderingOnBehalfUserData.userToOrderFor.allowance_type === AllowanceType.Monthly ? t('allowance.month') : undefined}
            className="ordering-on-behalf-input__allowance-price"
          />
        );
      } else {
        return t('allowance.missing');
      }
    } else if (orderingOnBehalfUserData.userToOrderFor?.allowance_type === AllowanceType.Unlimited) {
      return t('allowance.unlimited');
    } else {
      return t('allowance.undefined');
    }
  };

  const renderOrderForUser = () => (
    <>
      <Title size="xxs" color="gray800" margin={{ bottom: 'sm' }}>
        {t('OrderingOnBehalf.title')}:
      </Title>
      <Flex centeredVertically occupyVerticalSpace={false}>
        <Div className="ordering-on-behalf-input__select-user-input-container" data-testid="select-user-input-container">
          <SelectUserInput users={usersForOOB} onUserSelectCb={(user: User) => getSelectedOOBUser(user)} resetSelection={resetSelected} />
        </Div>
        <Button margin={{ left: 'md' }} variant="text" textVariantProps={{ color: 'gray400' }} onClick={clearSelection}>
          {t('OrderingOnBehalf.cancelButton')}
        </Button>
      </Flex>
      {orderingOnBehalfUserData.status && (
        <Div margin={{ top: 'md' }}>
          <Divider />
          <Flex spaceBetween margin={{ top: 'md' }}>
            <BodyText color="gray400">
              {orderingOnBehalfUserData.userToOrderFor?.name ? t('OrderingOnBehalf.nameAndGroupField') : t('OrderingOnBehalf.groupField')}
            </BodyText>
            <BodyText color="gray800">
              {orderingOnBehalfUserData.userToOrderFor?.name && `${orderingOnBehalfUserData.userToOrderFor.name}, `}
              {orderingOnBehalfUserData.userToOrderFor?.user_group?.name}
            </BodyText>
          </Flex>
          <Flex spaceBetween margin={{ top: 'xs' }}>
            <BodyText color="gray400">{t('OrderingOnBehalf.contactField')}</BodyText>
            <BodyText color="gray800">
              {orderingOnBehalfUserData.userToOrderFor?.phone_number}, {orderingOnBehalfUserData.userToOrderFor?.email}
            </BodyText>
          </Flex>
          <Flex spaceBetween margin={{ top: 'xs' }}>
            <BodyText color="gray400">{t('OrderingOnBehalf.allowanceField')}</BodyText>
            <BodyText tagName="div" color="gray800">
              {renderAllowance()}
            </BodyText>
          </Flex>
          <Flex spaceBetween margin={{ top: 'xs' }}>
            <BodyText color="gray400">{t('OrderingOnBehalf.deviceLimitField')}</BodyText>
            <BodyText color={isSingleDevicePolicyInEffectForUser('oob') ? 'red600' : 'gray800'}>
              {t(`OrderingOnBehalf.deviceLimitStatus.${orderingOnBehalfUserData.userToOrderFor?.policies?.single_device_policy}`)}
            </BodyText>
          </Flex>
        </Div>
      )}
    </>
  );

  const renderOrderForOrganization = () => (
    <>
      <Flex style={{ width: '100%' }} spaceBetween>
        <Title size="xxs" color="gray800" margin={{ bottom: 'sm' }}>
          {t('OrderingForOrganization.title')}:
        </Title>
        <Button variant="text" textVariantProps={{ color: 'gray400' }} onClick={clearSelection}>
          {t('OrderingForOrganization.cancelButton')}
        </Button>
      </Flex>
      <BodyText>{selectedOrganization?.name || ownOrganization?.name}</BodyText>
    </>
  );

  return (
    <Div margin={{ bottom: 'xlg' }} borderRadius={{ size: 'md' }} data-testid="ordering-on-behalf-element" backgroundColor={'white'}>
      <Div boxShadow="sm" padding={{ size: 'md' }} occupyVerticalSpace>
        {orderingForOrganization ? renderOrderForOrganization() : renderOrderForUser()}
      </Div>
    </Div>
  );
};

export default OrderingOnBehalfInput;
